import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Calendar, 
  FileText, 
  Settings, 
  MapPin, 
  Users, 
  UserCircle,
  LogOut,
  Menu,
  X,
  Building2,
  BarChart3,
  Shield,
  Zap,
  ChevronDown,
  Plus,
  RefreshCw,
  Crown,
  Flame,
  Lock
} from 'lucide-react';
import { Logo } from './Logo';
import { Language, useTranslation } from '../translations';
import { 
  getCurrentUserCompany, 
  isFeatureEnabled, 
  AVAILABLE_FEATURES, 
  supabase, 
  isMagicAdmin,
  fetchCompanies,
  linkUserToCompany,
  getCurrentUserRole,
  type Company 
} from '../lib/supabase';
import { CompanySelector } from './CompanySelector';

interface PlatformLayoutProps {
  children: React.ReactNode;
  currentSection: string;
  onSectionChange: (section: string) => void;
  onLogout: () => void;
}

export function PlatformLayout({ 
  children, 
  currentSection,
  onSectionChange,
  onLogout 
}: PlatformLayoutProps) {
  const { lang, setLang } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [isMagicAdminUser, setIsMagicAdminUser] = useState(false);
  const [isCompanySwitcherOpen, setIsCompanySwitcherOpen] = useState(false);
  const [isMobileCompanySwitcherOpen, setIsMobileCompanySwitcherOpen] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    try {
      setIsLoadingCompany(true);
      
      // Check if user is magic admin first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        console.log('No user found, showing company selector');
        setShowCompanySelector(true);
        setHasInitialized(true);
        return;
      }

      const magicAdmin = await isMagicAdmin(user.email);
      setIsMagicAdminUser(magicAdmin);

      // Get current user role
      await getCurrentUserRole(user.id);

      // Check if we have a stored company selection for this user
      const storedCompanyId = localStorage.getItem(`selectedCompany_${user.id}`);
      
      if (storedCompanyId && !magicAdmin) {
        // For regular users, try to load the stored company
        try {
          const { data: storedCompany, error } = await supabase
            .from('companies')
            .select(`
              *,
              features:company_features(*)
            `)
            .eq('id', storedCompanyId)
            .single();

          if (!error && storedCompany) {
            console.log('Loaded stored company:', storedCompany);
            setCompany(storedCompany);
            setShowCompanySelector(false);
            setHasInitialized(true);
            return;
          }
        } catch (err) {
          console.log('Failed to load stored company, will show selector');
          localStorage.removeItem(`selectedCompany_${user.id}`);
        }
      }

      // For magic admins or if no stored company, always show company selector first
      if (magicAdmin) {
        console.log('Magic admin detected, showing company selector');
        setShowCompanySelector(true);
        setHasInitialized(true);
        return;
      }

      // For regular users, try to load their company from profile
      const companyData = await getCurrentUserCompany();
      
      if (!companyData) {
        console.log('No company found, showing company selector');
        setShowCompanySelector(true);
      } else {
        console.log('Company loaded from profile:', companyData);
        setCompany(companyData);
        setShowCompanySelector(false);
        // Store the company selection
        localStorage.setItem(`selectedCompany_${user.id}`, companyData.id);
      }
      
      setHasInitialized(true);
    } catch (error) {
      console.error('Error loading company data:', error);
      setShowCompanySelector(true);
      setHasInitialized(true);
    } finally {
      setIsLoadingCompany(false);
    }
  };

  const getCurrentUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role:roles(name)')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        return;
      }

      if (data && data.role) {
        setCurrentUserRole(data.role.name);
      }
    } catch (err) {
      console.error('Error getting current user role:', err);
    }
  };

  const loadAvailableCompanies = async () => {
    if (!isMagicAdminUser) return;
    
    try {
      setIsLoadingCompanies(true);
      const companies = await fetchCompanies();
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const handleCompanySelected = async (selectedCompany: Company) => {
    console.log('Company selected:', selectedCompany);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Store the company selection in localStorage
        localStorage.setItem(`selectedCompany_${user.id}`, selectedCompany.id);
        
        // For magic admins, also link the user to the company
        if (isMagicAdminUser) {
          await linkUserToCompany(user.id, selectedCompany.id);
        }

        // Get current user role
        await getCurrentUserRole(user.id);
      }
      
      setCompany(selectedCompany);
      setShowCompanySelector(false);
      setIsCompanySwitcherOpen(false);
      setIsMobileCompanySwitcherOpen(false);
    } catch (error) {
      console.error('Error handling company selection:', error);
    }
  };

  const handleSwitchCompany = async (targetCompany: Company) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Store the new company selection
      localStorage.setItem(`selectedCompany_${user.id}`, targetCompany.id);

      // Link user to the new company if magic admin
      if (isMagicAdminUser) {
        await linkUserToCompany(user.id, targetCompany.id);
      }
      
      // Update the current company
      setCompany(targetCompany);
      setIsCompanySwitcherOpen(false);
      setIsMobileCompanySwitcherOpen(false);
      setIsMobileMenuOpen(false);

      // Get current user role
      await getCurrentUserRole(user.id);
    } catch (error) {
      console.error('Error switching company:', error);
    }
  };

  const handleShowCompanySelector = () => {
    setShowCompanySelector(true);
    setIsCompanySwitcherOpen(false);
    setIsMobileCompanySwitcherOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    // Clear stored company selection on logout
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      localStorage.removeItem(`selectedCompany_${user.id}`);
    }
    
    await supabase.auth.signOut();
    onLogout();
  };

  // Show loading screen until initialization is complete
  if (!hasInitialized || isLoadingCompany) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading platform...</p>
        </div>
      </div>
    );
  }

  // Show company selector if no company is loaded or if explicitly requested
  if (showCompanySelector) {
    return <CompanySelector onCompanySelected={handleCompanySelected} />;
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Company Access</h2>
          <p className="text-gray-600 mb-4">You don't have access to any companies.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleShowCompanySelector}
              className="px-4 py-2 border border-jaimy-red text-jaimy-red rounded-lg hover:bg-jaimy-red/5 transition-colors"
            >
              Select Company
            </button>
            <button
              onClick={handleLogout}
              className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Define navigation based on company features and user role
  const getNavigation = () => {
    const baseNavigation = [
      { name: 'Dashboard', icon: LayoutDashboard, id: 'dashboard' },
      { name: 'Book Service', icon: Calendar, id: 'book-service' },
      { name: 'Bookings', icon: FileText, id: 'bookings' }
    ];

    // Add feature-based navigation
    if (isFeatureEnabled(company, AVAILABLE_FEATURES.LOCATION_MANAGEMENT.key)) {
      baseNavigation.push({ name: 'Locations', icon: MapPin, id: 'locations' });
    }

    // Add HeatingComfort Pro section - only for admin and operational roles
    if (isFeatureEnabled(company, AVAILABLE_FEATURES.MAINTENANCE_CONTRACTS.key) && 
        (currentUserRole === 'admin' || currentUserRole === 'operational')) {
      baseNavigation.push({ name: 'HeatingComfort Pro', icon: Flame, id: 'heating-comfort' });
    }

    // Add User Management - only for admin role
    if (isFeatureEnabled(company, AVAILABLE_FEATURES.MULTI_USER_MANAGEMENT.key) && 
        currentUserRole === 'admin') {
      baseNavigation.push({ name: 'Users', icon: Users, id: 'users' });
    }

    // Add Reports - for admin, operational, and financial roles
    if (isFeatureEnabled(company, AVAILABLE_FEATURES.REPORTING_ANALYTICS.key) && 
        (currentUserRole === 'admin' || currentUserRole === 'operational' || currentUserRole === 'financial')) {
      baseNavigation.push({ name: 'Reports', icon: BarChart3, id: 'reports' });
    }

    // Add Invoicing - for admin and financial roles
    if (isFeatureEnabled(company, AVAILABLE_FEATURES.ADVANCED_INVOICING.key) && 
        (currentUserRole === 'admin' || currentUserRole === 'financial')) {
      baseNavigation.push({ name: 'Invoicing', icon: FileText, id: 'invoicing' });
    }

    // Add API Integration - only for admin role
    if (isFeatureEnabled(company, AVAILABLE_FEATURES.API_INTEGRATION.key) && 
        currentUserRole === 'admin') {
      baseNavigation.push({ name: 'Integrations', icon: Zap, id: 'integrations' });
    }

    // Add Settings - only for admin role
    if (currentUserRole === 'admin') {
      baseNavigation.push({ name: 'Settings', icon: Settings, id: 'settings' });
    }

    return baseNavigation;
  };

  const navigation = getNavigation();

  const languages: Record<Language, string> = {
    NL: 'Nederlands',
    FR: 'Français',
    EN: 'English'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden md:flex md:w-64 flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-200">
          {/* Logo Section */}
          <div className="flex flex-col flex-shrink-0 pt-8 pb-6 border-b border-gray-200">
            <div className="px-4 flex justify-center">
              <Logo showSubtitle={true} />
            </div>
            {/* Company Info with Switcher */}
            <div className="px-4 mt-4">
              <div className="relative">
                <button
                  onClick={() => {
                    if (isMagicAdminUser) {
                      setIsCompanySwitcherOpen(!isCompanySwitcherOpen);
                      if (!isCompanySwitcherOpen) {
                        loadAvailableCompanies();
                      }
                    }
                  }}
                  className={`w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg ${
                    isMagicAdminUser ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'
                  } transition-colors`}
                >
                  <div 
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: company.branding.primary_color }}
                  >
                    {company.branding.logo_url ? (
                      <img 
                        src={company.branding.logo_url} 
                        alt={company.name}
                        className="h-6 w-6 object-contain"
                      />
                    ) : (
                      company.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{company.subscription_tier}</p>
                  </div>
                  {isMagicAdminUser && (
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                      isCompanySwitcherOpen ? 'rotate-180' : ''
                    }`} />
                  )}
                </button>

                {/* Company Switcher Dropdown */}
                {isMagicAdminUser && isCompanySwitcherOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    <div className="p-2">
                      <button
                        onClick={handleShowCompanySelector}
                        className="w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                          <Plus className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Create New Company</p>
                          <p className="text-xs text-gray-500">Set up a new company account</p>
                        </div>
                      </button>
                      
                      {isLoadingCompanies ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                        </div>
                      ) : (
                        <>
                          <div className="border-t border-gray-100 my-2"></div>
                          {availableCompanies.map((availableCompany) => (
                            <button
                              key={availableCompany.id}
                              onClick={() => handleSwitchCompany(availableCompany)}
                              className={`w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors ${
                                availableCompany.id === company.id ? 'bg-jaimy-red/5 border border-jaimy-red/20' : ''
                              }`}
                            >
                              <div 
                                className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                                style={{ backgroundColor: availableCompany.branding.primary_color }}
                              >
                                {availableCompany.branding.logo_url ? (
                                  <img 
                                    src={availableCompany.branding.logo_url} 
                                    alt={availableCompany.name}
                                    className="h-6 w-6 object-contain"
                                  />
                                ) : (
                                  availableCompany.name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{availableCompany.name}</p>
                                <p className="text-xs text-gray-500 capitalize">{availableCompany.subscription_tier}</p>
                              </div>
                              {availableCompany.id === company.id && (
                                <div className="h-2 w-2 rounded-full bg-jaimy-red"></div>
                              )}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className="flex-1 px-2 py-4 space-y-1">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSectionChange(item.id)}
                  className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentSection === item.id
                      ? 'bg-jaimy-red text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 ${
                    currentSection === item.id
                      ? 'text-white'
                      : 'text-gray-400 group-hover:text-jaimy-red'
                  }`} />
                  {item.name}
                </button>
              ))}

              {/* Disabled navigation items for property managers */}
              {currentUserRole === 'property_manager' && (
                <>
                  {!isFeatureEnabled(company, AVAILABLE_FEATURES.MAINTENANCE_CONTRACTS.key) || (
                    !navigation.some(item => item.id === 'heating-comfort') && (
                      <div className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed relative">
                        <Flame className="mr-3 h-5 w-5 text-gray-300" />
                        <span>HeatingComfort Pro</span>
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <Lock className="h-4 w-4 text-gray-300" title="Contact admin for access" />
                        </div>
                      </div>
                    )
                  )}

                  {!navigation.some(item => item.id === 'users') && (
                    <div className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed relative">
                      <Users className="mr-3 h-5 w-5 text-gray-300" />
                      <span>Users</span>
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Lock className="h-4 w-4 text-gray-300" title="Contact admin for access" />
                      </div>
                    </div>
                  )}

                  {!navigation.some(item => item.id === 'settings') && (
                    <div className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed relative">
                      <Settings className="mr-3 h-5 w-5 text-gray-300" />
                      <span>Settings</span>
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Lock className="h-4 w-4 text-gray-300" title="Contact admin for access" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </nav>
            
            {/* Language and Logout Section */}
            <div className="flex-shrink-0 border-t border-gray-200">
              <div className="px-2 py-3">
                <div className="flex space-x-2">
                  {(Object.keys(languages) as Language[]).map((code) => (
                    <button
                      key={code}
                      onClick={() => setLang(code)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        lang === code
                          ? 'bg-jaimy-red text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-2 pb-3">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile menu button */}
      <div className="md:hidden fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="gradient-bg p-2 rounded-lg text-white"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`
        fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden transition-opacity
        ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
      `}>
        <div className={`
          fixed inset-y-0 left-0 w-64 bg-white transform transition-transform
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="flex flex-col h-full">
            <div className="flex flex-col flex-shrink-0 pt-8 pb-6 border-b border-gray-200">
              <div className="px-4 flex justify-center">
                <Logo showSubtitle={true} />
              </div>
              {/* Mobile Company Info with Switcher */}
              <div className="px-4 mt-4">
                <div className="relative">
                  <button
                    onClick={() => {
                      if (isMagicAdminUser) {
                        setIsMobileCompanySwitcherOpen(!isMobileCompanySwitcherOpen);
                        if (!isMobileCompanySwitcherOpen) {
                          loadAvailableCompanies();
                        }
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg ${
                      isMagicAdminUser ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'
                    } transition-colors`}
                  >
                    <div 
                      className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: company.branding.primary_color }}
                    >
                      {company.branding.logo_url ? (
                        <img 
                          src={company.branding.logo_url} 
                          alt={company.name}
                          className="h-6 w-6 object-contain"
                        />
                      ) : (
                        company.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{company.subscription_tier}</p>
                    </div>
                    {isMagicAdminUser && (
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                        isMobileCompanySwitcherOpen ? 'rotate-180' : ''
                      }`} />
                    )}
                  </button>

                  {/* Mobile Company Switcher Dropdown */}
                  {isMagicAdminUser && isMobileCompanySwitcherOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                      <div className="p-2">
                        <button
                          onClick={handleShowCompanySelector}
                          className="w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                            <Plus className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Create New Company</p>
                            <p className="text-xs text-gray-500">Set up a new company account</p>
                          </div>
                        </button>
                        
                        {isLoadingCompanies ? (
                          <div className="flex items-center justify-center py-4">
                            <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                          </div>
                        ) : (
                          <>
                            <div className="border-t border-gray-100 my-2"></div>
                            {availableCompanies.map((availableCompany) => (
                              <button
                                key={availableCompany.id}
                                onClick={() => handleSwitchCompany(availableCompany)}
                                className={`w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg transition-colors ${
                                  availableCompany.id === company.id ? 'bg-jaimy-red/5 border border-jaimy-red/20' : ''
                                }`}
                              >
                                <div 
                                  className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                                  style={{ backgroundColor: availableCompany.branding.primary_color }}
                                >
                                  {availableCompany.branding.logo_url ? (
                                    <img 
                                      src={availableCompany.branding.logo_url} 
                                      alt={availableCompany.name}
                                      className="h-6 w-6 object-contain"
                                    />
                                  ) : (
                                    availableCompany.name.charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{availableCompany.name}</p>
                                  <p className="text-xs text-gray-500 capitalize">{availableCompany.subscription_tier}</p>
                                </div>
                                {availableCompany.id === company.id && (
                                  <div className="h-2 w-2 rounded-full bg-jaimy-red"></div>
                                )}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onSectionChange(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentSection === item.id
                      ? 'bg-jaimy-red text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 ${
                    currentSection === item.id
                      ? 'text-white'
                      : 'text-gray-400 group-hover:text-jaimy-red'
                  }`} />
                  {item.name}
                </button>
              ))}

              {/* Disabled navigation items for property managers */}
              {currentUserRole === 'property_manager' && (
                <>
                  {!isFeatureEnabled(company, AVAILABLE_FEATURES.MAINTENANCE_CONTRACTS.key) || (
                    !navigation.some(item => item.id === 'heating-comfort') && (
                      <div className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed relative">
                        <Flame className="mr-3 h-5 w-5 text-gray-300" />
                        <span>HeatingComfort Pro</span>
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <Lock className="h-4 w-4 text-gray-300" title="Contact admin for access" />
                        </div>
                      </div>
                    )
                  )}

                  {!navigation.some(item => item.id === 'users') && (
                    <div className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed relative">
                      <Users className="mr-3 h-5 w-5 text-gray-300" />
                      <span>Users</span>
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Lock className="h-4 w-4 text-gray-300" title="Contact admin for access" />
                      </div>
                    </div>
                  )}

                  {!navigation.some(item => item.id === 'settings') && (
                    <div className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 cursor-not-allowed relative">
                      <Settings className="mr-3 h-5 w-5 text-gray-300" />
                      <span>Settings</span>
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Lock className="h-4 w-4 text-gray-300" title="Contact admin for access" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </nav>
            
            {/* Mobile Language and Logout Section */}
            <div className="flex-shrink-0 border-t border-gray-200">
              <div className="px-2 py-3">
                <div className="flex space-x-2">
                  {(Object.keys(languages) as Language[]).map((code) => (
                    <button
                      key={code}
                      onClick={() => setLang(code)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        lang === code
                          ? 'bg-jaimy-red text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-2 pb-3">
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64">
        <main className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            {/* Pass company to children if it's CompanyDashboard */}
            {React.cloneElement(children as React.ReactElement, { company })}
          </div>
        </main>
      </div>
    </div>
  );
}